import {
    ApplicationParametersDefinition,
    AssemblyApplicationCreateRequest,
    AssemblyApplicationTypeUpdateRequest,
    AssemblyApplicationUpdateRequest,
    FieldElementUnion,
    IAssemblyApplicationTypeUpdateRequest,
    IAssemblyApplicationUpdateRequest,
    InputMode,
} from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';
import { AssemblyApplicationFormData } from 'pages/Settings/AssemblyType/AssemblyApplicationFormContent';
import { removeUndefinedProperties } from 'utils/object';
import { Lookup } from 'utils/types';

export type ParameterType = 'bool' | 'integer' | 'decimal' | 'text' | 'selection';

export const formattedParameterTypes: Lookup<ParameterType, string> = {
    bool: 'Toggle',
    integer: 'Integer',
    decimal: 'Decimal',
    text: 'Text',
    selection: 'Selection',
};

export const parameterTypeToInputMode: Lookup<ParameterType, InputMode> = {
    text: InputMode.Raw,
    bool: InputMode.Raw,
    integer: InputMode.Raw,
    decimal: InputMode.Raw,
    selection: InputMode.Select,
};

/**
 * Updates a assembly type given its id and the updates to make. Loads the
 * assembly type to get all current information and adds the updates. Helps prevent getting
 * out of sync with the api or not passing in fields and accidentally clearing them.
 * @param assemblyTypeId The id of the assembly type to update
 * @param updates The fields to update on the assembly type
 */
export async function updateAssemblyType(
    assemblyTypeId: number,
    updates: Partial<IAssemblyApplicationTypeUpdateRequest>
) {
    const { Name, Active } = await sdClient.getAssemblyApplicationType({
        applicationTypeId: assemblyTypeId,
    });

    const request = new AssemblyApplicationTypeUpdateRequest({
        Name,
        Active,
        ...removeUndefinedProperties(updates),
    });

    return sdClient.updateAssemblyApplicationType({
        applicationTypeId: assemblyTypeId,
        body: request,
    });
}

/**
 * Updates an assembly application given its id and the updates to make. Loads the
 * application to get all current information and adds the updates. Helps prevent getting
 * out of sync with the api or not passing in fields and accidentally clearing them.
 * @param applicationId The id of the assembly application to update
 * @param updateTransformer A function that mutates the request, adding the desired field updates
 */
export async function updateAssemblyApplication(
    applicationId: number,
    updateTransformer: (req: IAssemblyApplicationUpdateRequest) => void
) {
    const {
        Name,
        Type,
        ParametersDefinition,
        ProbableFaultModes,
    } = await sdClient.getAssemblyApplication({ applicationId });

    const request = new AssemblyApplicationUpdateRequest({
        Name,
        TypeId: Type.Id,
        ProbableFaultModeIds: ProbableFaultModes?.map((mode) => mode.Id ?? 1),
        ParametersDefinition,
    });

    updateTransformer(request);

    return sdClient.updateAssemblyApplication({ applicationId, body: request });
}

/**
 * Creates or updates an assembly application parameter given its application and the new parameter.
 * Loads all the assembly parameters to get all current information and adds the new parameter.
 * @param applicationId The id of the application the parameter belongs to
 * @param parameter The assembly application parameter
 */
export async function createOrUpdateAssemblyParameter(
    applicationId: number,
    parameter: FieldElementUnion
) {
    return updateAssemblyApplication(applicationId, (request) => {
        const parametersDefinitionData = request.ParametersDefinition?.Data;
        request.ParametersDefinition = new ApplicationParametersDefinition({
            Data: {
                ...parametersDefinitionData,
                [parameter.FieldKey]: parameter,
            },
        });
    });
}

/**
 * Deletes an assembly application parameter given its application and the parameter code.
 * Loads all the assembly parameters to get all current information and
 * removes the parameter with the code.
 * @param applicationId The id of the application the parameter belongs to
 * @param parameterKey The assembly application parameter code to be removed
 */
export async function deleteAssemblyParameter(
    applicationId: number,
    parameterKey: string
) {
    return updateAssemblyApplication(applicationId, (request) => {
        const { [parameterKey]: removed, ...parametersDefinitionData } =
            request.ParametersDefinition?.Data ?? {};
        request.ParametersDefinition = new ApplicationParametersDefinition({
            Data: {
                ...parametersDefinitionData,
            },
        });
    });
}

export async function duplicateAssemblyApplication(
    applicationId: number,
    assemblyTypeId: number,
    data: AssemblyApplicationFormData
) {
    const application = await sdClient.getAssemblyApplication({
        applicationId,
    });

    return sdClient.createAssemblyApplication({
        body: new AssemblyApplicationCreateRequest({
            ...application,
            TypeId: assemblyTypeId,
            Name: data.name.trim(),
            Code: data.code.trim(),
        }),
    });
}
